import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Align,
  Arrange,
  Container,
  FormField,
  IconButton,
  Menu,
  MenuItem,
  Pill,
  SvgClose,
  Text,
} from '@loomhq/lens';
import Downshift from 'downshift';
import pluralize from 'pluralize';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLayer } from 'react-laag';
import { useDispatch } from 'react-redux';
import ResizeObserver from 'resize-observer-polyfill';

import { openModal } from 'actions/modals';
import {
  HEURISTIC_TAG_DROPDOWN_SELECTED,
  HEURISTIC_TAG_DROPDOWN_SHOWN,
} from 'constants/events';
import { SUGGEST_PUBLISHING_PROMPT } from 'constants/modalTypes';
import { UserPropertyEnum } from 'constants/userProperties';
import { VideoVisibility } from 'constants/visibility';
import { track } from 'utilities/analytics';

import { useUserProperty } from 'hooks/userProperties';

import AddTagToVideoMutation from './AddTagToVideoMutation.graphql';
import GeneratedMetadata from './generated-metadata/GeneratedMetadata';
import GetTagsByVideoIdQuery from './GetTagsByVideoIdQuery.graphql';
import ReadOnlyTagPills from './read-only-tag-pills';
import RemoveTagFromVideoMutation from './RemoveTagFromVideoMutation.graphql';
import SearchWorkspaceTags from './SearchWorkspaceTags.graphql';
import TagLink from './tag-link';
import useSuggestedTags from './useSuggestedTags';
import { VIDEO_PRIVACY_OWNER } from '../../../../../shared/constants/video';
import { INVALID_TAG_CHAR_REGEX } from '../../../../../shared/constants/workspaceTags';

import styles from './styles.module.less';

const MAX_TAGS = 10;
const MAX_TAG_LENGTH = 20;
const SEARCH_RESULT_LIMIT = 8;

const formatOption = (item, input) => {
  const { count, tag } = item;

  const indexStart = tag.indexOf(input);
  const indexEnd = indexStart + input.length;

  const content =
    indexStart < 0 ? (
      tag
    ) : (
      <>
        {tag.substring(0, indexStart)}
        <span className="weight:medium">
          {tag.substring(indexStart, indexEnd)}
        </span>
        {tag.substring(indexEnd)}
      </>
    );

  return (
    <>
      <Text>
        <span className="weight:medium">{`#`}</span>
        {content}
      </Text>
      <Text color="bodyDimmed">
        {count} {pluralize('videos', count)}
      </Text>
    </>
  );
};

const formatNewTag = input => (
  <>
    <Text>
      <span className="weight:medium">
        {`#`}
        {input.toLocaleLowerCase()}
      </span>
    </Text>
    <Text color="bodyDimmed">Create new tag</Text>
  </>
);

const sanitizeText = input => {
  return input.trim().toLocaleLowerCase();
};

const TagsTypeahead = ({ initialTags, video }) => {
  const [hasEdited, setHasEdited] = useState(false);
  const { visibility, id: videoId } = video;
  const dispatch = useDispatch();
  const [selectedItems, setSelectedItems] = useState(initialTags);
  const [options, setOptions] = useState([]);
  const [hasOptions, setHasOptions] = useState(false);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [suggestedPublishing, setSuggestedPublishing] = useState(false);

  useEffect(() => {
    setSelectedItems(initialTags);
  }, [initialTags]);

  const [errorMessage, setErrorMessage] = useState('');
  const isPublished = [
    VideoVisibility.WORKSPACE,
    VideoVisibility.PUBLIC,
  ].includes(visibility);

  const inputRef = useRef(null);
  const emptyInputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  const [downshiftIsOpen, setDownshiftIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectedItemsSet = useMemo(
    () => new Set(selectedItems),
    [selectedItems]
  );
  const suggestedTags = useSuggestedTags(selectedItemsSet);
  const [hasFocused, setHasFocused] = useState(false);
  const showSuggestedTags = suggestedTags.length > 0 && !inputValue;
  const showDropdown = showSuggestedTags || hasOptions;

  const [runQuery] = useLazyQuery(SearchWorkspaceTags, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const optionMatchesExactly = data.result.tags.some(
        option => option.tag === inputValue
      );

      const typeaheadOptions = data.result.tags.filter(
        option => !selectedItemsSet.has(option.tag)
      );

      setOptions(typeaheadOptions);
      setShowCreateNew(!optionMatchesExactly);
      setHasOptions(typeaheadOptions.length || !optionMatchesExactly);
    },
  });

  const { value: shouldSuggestPublishAgain } = useUserProperty(
    UserPropertyEnum.SUGGEST_PUBLISHING_ON_TAG
  );

  const handleBlur = e => {
    const currentTarget = e.currentTarget;
    const isPrivate = video.privacy === VIDEO_PRIVACY_OWNER;

    // Give browser time to focus the next element
    // https://muffinman.io/blog/catching-the-blur-event-on-an-element-and-its-children/
    requestAnimationFrame(() => {
      // Check if the new focused element is a child of the original container
      if (
        initialTags.length === 0 &&
        video.currentUserCanEdit &&
        !isPrivate &&
        !currentTarget.contains(document.activeElement) &&
        !isPublished &&
        hasEdited &&
        !suggestedPublishing &&
        shouldSuggestPublishAgain === true
      ) {
        dispatch(
          openModal(SUGGEST_PUBLISHING_PROMPT, {
            video,
          })
        );
        setSuggestedPublishing(true);
        setHasEdited(false);
      }
    });
  };

  useEffect(() => {
    if (!inputValue) {
      setOptions([]);
      setHasOptions(false);
      setErrorMessage('');
    }

    if (inputValue?.length > 0) {
      const selectedItemsSet = new Set(selectedItems);

      if (selectedItemsSet.has(inputValue)) {
        setErrorMessage(`#${inputValue} already exists.`);
      } else if (inputValue.length > MAX_TAG_LENGTH) {
        setErrorMessage(`Tags can be up to ${MAX_TAG_LENGTH} characters long.`);
      } else if (inputValue.match(INVALID_TAG_CHAR_REGEX)?.length > 0) {
        setErrorMessage(
          `Tags can consist of alphanumeric characters (A-Z, 0-9)`
        );
      } else {
        runQuery({
          variables: {
            query: inputValue,
            limit: SEARCH_RESULT_LIMIT,
          },
        });
        setErrorMessage('');
      }
    }
  }, [inputValue, errorMessage, runQuery, selectedItems]);

  const [removeTagFromVideo] = useMutation(RemoveTagFromVideoMutation, {
    onCompleted: data => {
      if (data.result?.tag) {
        setSelectedItems(selectedItems.filter(i => i !== data.result.tag));
        setHasEdited(true);
        inputRef?.current?.focus();
      }
    },
  });

  const removeTag = tag => {
    removeTagFromVideo({
      variables: {
        videoId,
        tag,
        context: 'Share page',
      },
    });

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const [addTagToVideo] = useMutation(AddTagToVideoMutation, {
    onCompleted: data => {
      if (data.result?.tag) {
        const newSelectedItems = [...selectedItems, data.result?.tag];

        setSelectedItems(newSelectedItems);
        setHasEdited(true);
        setInputValue('');

        newSelectedItems.length < MAX_TAGS
          ? inputRef?.current?.focus()
          : emptyInputRef?.current?.focus();
      }
    },
  });

  const addTag = input => {
    const tag = input?.tag ? input.tag : input;

    if (!errorMessage) {
      addTagToVideo({
        variables: {
          videoId,
          tag,
          context: showSuggestedTags
            ? 'Share page suggested tags'
            : 'Share page',
        },
      });

      if (showSuggestedTags) {
        track(HEURISTIC_TAG_DROPDOWN_SELECTED, {
          videoId,
          tag_id: tag,
          recommendation_type: 'heuristic',
          heuristic_type: 'recent_popular',
          position: suggestedTags.indexOf(tag),
        });
      }
    }
  };

  const onKeyDown = e => {
    if (e.key === 'Backspace' && !inputValue && selectedItems.length > 0) {
      removeTag(selectedItems[selectedItems.length - 1]);
    }

    if ((e.key === 'Spacebar' || e.key === ' ') && inputValue) {
      addTag(options[selectedIndex] ?? inputValue);
    }

    if (e.key === 'Tab' && downshiftIsOpen) {
      e.preventDefault();
      const newIndex = e.shiftKey ? selectedIndex - 1 : selectedIndex + 1;
      const total = showCreateNew ? options.length + 1 : options.length;

      let finalIndex = newIndex;

      if (newIndex >= total) {
        finalIndex = 0;
      } else if (newIndex < 0) {
        finalIndex = total - 1;
      }

      setDownshiftIsOpen(false);
      setSelectedIndex(finalIndex);
    }
  };

  const onInputFocus = () => {
    setInputFocused(true);
    setDownshiftIsOpen(true);

    if (!hasFocused) {
      setHasFocused(true);

      if (showSuggestedTags) {
        track(HEURISTIC_TAG_DROPDOWN_SHOWN, {
          video_id: videoId,
          recommendation_type: 'heuristic',
          heuristic_type: 'recent_popular',
          total_results_shown: suggestedTags.length,
        });
      }
    }
  };

  const checkBackspace = (e, selectedItem) => {
    return e.key === 'Backspace' && removeTag(selectedItem);
  };

  const stateReducer = (state, changes) => {
    setSelectedIndex(changes.highlightedIndex);

    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownEnter:
      case Downshift.stateChangeTypes.clickItem: {
        addTag(changes.selectedItem);

        return changes;
      }
      default:
        if (changes.isOpen !== undefined) {
          setDownshiftIsOpen(changes.isOpen);
        }

        return changes;
    }
  };

  const { layerProps, triggerProps, renderLayer, triggerBounds } = useLayer({
    isOpen: downshiftIsOpen && !errorMessage,
    ResizeObserver,
    placement: 'bottom-start',
    auto: true,
    snap: true,
  });

  return (
    <Downshift
      itemToString={item => (item ? item.tag : '')}
      stateReducer={stateReducer}
      defaultHighlightedIndex={0}
      highlightedIndex={selectedIndex}
      isOpen={downshiftIsOpen}
    >
      {({ getInputProps, getMenuProps, getItemProps, highlightedIndex }) => (
        <div>
          <div {...triggerProps}>
            <FormField
              labelFor="tags"
              errorMessage={errorMessage}
              onClick={() => {
                if (!downshiftIsOpen) {
                  inputRef?.current?.focus() ?? emptyInputRef?.current?.focus();
                }
              }}
              onBlur={handleBlur}
            >
              <Container
                paddingTop="small"
                paddingX="medium"
                radius="medium"
                borderColor={downshiftIsOpen && 'primary'}
                className={`${styles.tagsList} items:center ${
                  inputFocused ? styles.tagsListActive : ''
                } ${errorMessage ? styles.tagsListError : ''}`}
              >
                <Arrange justifyContent="space-between" gap="xsmall">
                  <Container className="flex flexWrap">
                    {selectedItems.map((selectedItem, index) => (
                      <Pill
                        key={index}
                        className={`${styles.pillCreator} mr:xsmall mb:small`}
                        backgroundColor="var(--tagBackground)"
                      >
                        <Arrange gap="xsmall">
                          <TagLink
                            tag={selectedItem}
                            onKeyDown={e => checkBackspace(e, selectedItem)}
                          />
                          <IconButton
                            altText="Close"
                            icon={<SvgClose />}
                            size="small"
                            onClick={() => removeTag(selectedItem)}
                            onKeyDown={e => checkBackspace(e, selectedItem)}
                          />
                        </Arrange>
                      </Pill>
                    ))}
                    <Pill
                      className={`mb:small ${styles.emptyPill} ${
                        selectedItems.length == MAX_TAGS
                          ? styles.visible
                          : styles.hidden
                      }`}
                      color="bodyDimmed"
                    >
                      <input
                        {...getInputProps({
                          ref: emptyInputRef,
                          maxLength: 0,
                          onKeyDown,
                          onFocus: () => setInputFocused(true),
                          onBlur: () => setInputFocused(false),
                        })}
                        className={`${styles.input} ${styles.emptyInput}`}
                        value=""
                      />
                    </Pill>
                    <Pill
                      className={`mr:xsmall mb:small ${styles.inputPill} ${
                        selectedItems.length < MAX_TAGS
                          ? styles.visible
                          : styles.hidden
                      }`}
                      color="bodyDimmed"
                      data-value={inputValue}
                    >
                      <Text color={inputFocused ? 'body' : 'bodyDimmed'}>
                        {`#`}
                      </Text>
                      <input
                        {...getInputProps({
                          ref: inputRef,
                          onKeyDown,
                          onChange: ({ target }) =>
                            setInputValue(sanitizeText(target.value)),
                          onFocus: onInputFocus,
                          onBlur: () => {
                            setInputFocused(false);
                            setInputValue('');
                          },
                        })}
                        placeholder="add tag"
                        className={styles.input}
                        value={inputValue}
                        size={6}
                      />
                    </Pill>
                  </Container>
                  {inputFocused && (
                    <Container height="100%" className="mb:small">
                      <Align alignment="bottomRight">
                        <Text color="bodyDimmed">
                          {selectedItems.length}/{MAX_TAGS}
                        </Text>
                      </Align>
                    </Container>
                  )}
                </Arrange>
              </Container>
            </FormField>
          </div>
          {downshiftIsOpen &&
            !errorMessage &&
            renderLayer(
              <div
                {...layerProps}
                style={{
                  ...layerProps.style,
                  width: triggerBounds.width,
                  zIndex: 1100,
                }}
              >
                {showDropdown && (
                  <Menu downshiftMenuProps={getMenuProps}>
                    {inputValue &&
                      options.map((item, index) => (
                        <MenuItem
                          key={index}
                          isHighlighted={highlightedIndex === index}
                          {...getItemProps({
                            item,
                            index,
                          })}
                        >
                          {formatOption(item, inputValue)}
                        </MenuItem>
                      ))}
                    {!inputValue &&
                      suggestedTags.map((item, index) => (
                        <MenuItem
                          key={index}
                          isHighlighted={highlightedIndex === index}
                          {...getItemProps({
                            item,
                            index,
                          })}
                        >
                          {formatOption(item, inputValue)}
                        </MenuItem>
                      ))}
                    {inputValue && showCreateNew && (
                      <MenuItem
                        isHighlighted={highlightedIndex === options.length}
                        hasDivider={options.length !== 0}
                        {...getItemProps({
                          index: options.length,
                          item: inputValue,
                        })}
                      >
                        {formatNewTag(inputValue)}
                      </MenuItem>
                    )}
                  </Menu>
                )}
              </div>
            )}
        </div>
      )}
    </Downshift>
  );
};

const TagsSection = ({ video, canEdit }) => {
  const { id: videoId } = video;
  const { loading, data } = useQuery(GetTagsByVideoIdQuery, {
    variables: { videoId },
  });
  const tags = data?.result?.tags;

  if (loading) {
    return null;
  }

  if (canEdit && tags) {
    return (
      <>
        <div className={`${styles.tagWrapper} my:small`}>
          <TagsTypeahead initialTags={tags} video={video} />
        </div>
        <GeneratedMetadata
          userCanEditTags
          videoId={videoId}
          existingTags={tags}
        />
      </>
    );
  }

  return (
    <>
      <ReadOnlyTagPills tags={tags} />
      {canEdit && <GeneratedMetadata videoId={videoId} existingTags={tags} />}
    </>
  );
};

export default TagsSection;
